import { useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import type { LivePlotHandle, LivePlotImageOptions } from '../components/LivePlot';
import type { ChartImage } from '../types/chartTypes';
import { collectLivePlotImages } from '../utils/livePlotSnapshots';
import { usePreparedPrint } from './usePreparedPrint';

type LivePlotPrintResult<K extends string> = {
  plotData?: Partial<Record<K, unknown>>;
  charts?: Partial<Record<K, ChartImage>>;
};

type BuildChartImagesOptions<K extends string> = {
  force?: boolean;
  getImageOptions?: (key: K) => LivePlotImageOptions | undefined;
};

type UseLivePlotPrintImagesOptions<K extends string, TResponse extends LivePlotPrintResult<K>> = {
  analysisResult: TResponse | null;
  setAnalysisResult: Dispatch<SetStateAction<TResponse | null>>;
  getImageOptions?: (key: K) => LivePlotImageOptions | undefined;
  errorMessage: string;
  enableScreenshot?: boolean;
};

// Keeps Plotly print/export wiring out of app components while leaving each app in control
// of its chart grid, print layout, chart order, and export dimensions.
export function useLivePlotPrintImages<K extends string, TResponse extends LivePlotPrintResult<K>>({ analysisResult, setAnalysisResult, getImageOptions, errorMessage, enableScreenshot = false }: UseLivePlotPrintImagesOptions<K, TResponse>) {
  const chartRefs = useRef<Partial<Record<K, LivePlotHandle | null>>>({});
  const printRootRef = useRef<HTMLDivElement | null>(null);

  const buildChartImages = useCallback(
    async (result: TResponse, options: BuildChartImagesOptions<K> = {}) => {
      // `plotData` is the backend contract. `charts` is frontend-owned cache data
      // generated from mounted LivePlot instances for print/save workflows.
      const plotKeys = Object.keys(result.plotData ?? {}) as K[];

      return collectLivePlotImages({
        keys: plotKeys,
        chartRefs,
        existingCharts: result.charts,
        force: options.force,
        getImageOptions: options.getImageOptions ?? getImageOptions
      });
    },
    [getImageOptions]
  );

  usePreparedPrint({
    rootRef: printRootRef,
    prepare: async () => {
      if (!analysisResult?.plotData) return;

      const charts = await buildChartImages(analysisResult, { force: true });
      // Browser print reads the hidden portal immediately after preparation, so the
      // generated images must be committed synchronously before the print dialog opens.
      flushSync(() => {
        setAnalysisResult({ ...analysisResult, charts });
      });
    },
    errorMessage,
    enableScreenshot,
    screenshotReady: Boolean(analysisResult?.plotData)
  });

  return { chartRefs, printRootRef, buildChartImages };
}
